import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { getUserSession, isSuperAdmin } from '../services/authService';
import { getChildAgeLabel, getChildGenderLabel, searchPatients } from '../services/profileService';
import { getPatientLinkLabel } from '../constants/childInterests';
import { getAgendaCountry } from '../constants/agendaTherapyTypes';
import '../styles/admin.css';

const getCountryLabel = (paisId) => {
  const match = getAgendaCountry(paisId);
  return match ? match.label : '—';
};

const AdminPatientsPage = () => {
  const history = useHistory();
  const session = getUserSession();
  const sessionId = session ? session.id : null;
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      history.replace('/iniciar-sesion');
      return;
    }

    if (!isSuperAdmin(session) && session.tipo !== 'especialista' && session.tipo !== 'administrativo') {
      history.replace('/');
    }
  }, [history, session, sessionId]);

  if (!sessionId) {
    return null;
  }

  const handleSearch = (event) => {
    event.preventDefault();
    const term = query.trim();

    if (term.length < 2) {
      setError('Ingresa al menos 2 caracteres (nombre o cédula).');
      return;
    }

    setLoading(true);
    setError('');
    setSearched(true);

    searchPatients(sessionId, term)
      .then((list) => {
        setResults(list);
      })
      .catch((requestError) => {
        setResults([]);
        setError(requestError.message || 'No se pudo realizar la búsqueda.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="admin-root">
      <div className="admin-layout">
        <header className="admin-header">
          <div>
            <h1>Buscar pacientes</h1>
            <p className="admin-muted">
              Busca por nombre o cédula. La vista muestra la ficha del paciente.
            </p>
          </div>
          <Link to="/admin/usuarios" className="admin-btn">
            Volver a usuarios
          </Link>
        </header>

        <form className="admin-card" onSubmit={handleSearch}>
          <label className="admin-field">
            <span>Nombre o cédula</span>
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Ej. Genesis o 8-123-456"
            />
          </label>
          <button type="submit" className="admin-btn admin-btn--primary" disabled={loading}>
            {loading ? 'Buscando…' : 'Buscar'}
          </button>
        </form>

        {error ? <p className="admin-alert admin-alert--error">{error}</p> : null}

        <div className="admin-card admin-card--table">
          <h2>Resultados</h2>
          {!searched ? <p className="admin-muted">Realiza una búsqueda para ver resultados.</p> : null}
          {searched && !loading && results.length === 0 ? (
            <p className="admin-muted">No se encontraron pacientes.</p>
          ) : null}
          {results.length > 0 ? (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Paciente</th>
                    <th>Documento</th>
                    <th>País</th>
                    <th>Edad</th>
                    <th>Género</th>
                    <th>Vínculo</th>
                    {isSuperAdmin(session) ? <th>Cuenta</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {results.map((patient) => (
                    <tr key={patient.id}>
                      <td>{patient.nombre || '—'}</td>
                      <td>{patient.documento_numero || '—'}</td>
                      <td>{getCountryLabel(patient.pais)}</td>
                      <td>{getChildAgeLabel(patient.fecha_nacimiento)}</td>
                      <td>{getChildGenderLabel(patient.genero)}</td>
                      <td>{getPatientLinkLabel(patient.vinculo)}</td>
                      {isSuperAdmin(session) ? (
                        <td>{patient.representante_nombre || patient.representante_email || '—'}</td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AdminPatientsPage;
